/** Kanban da esteira: posts agrupados por status. Onda 16. */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import type { PostDTO, PostStatus } from "@unik/shared";
import { statusLabel } from "@unik/shared";
import { api } from "../../lib/api";
import { AdminShell } from "../../ui/AdminShell";
import { Button } from "../../ui/Button";
import { PurposeBadge } from "../../ui/Badge";
import { Skeleton } from "../../ui/Card";

const COLUMNS: PostStatus[] = [
  "draft",
  "pending_approval",
  "revision_requested",
  "approved",
  "published",
];

export function Kanban() {
  const { id } = useParams();
  const [posts, setPosts] = useState<PostDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<PostDTO[]>(`/clients/${id}/posts`)
      .then(setPosts)
      .catch(() => setError("Não foi possível carregar o fluxo."));
  }, [id]);

  return (
    <AdminShell>
      <div className="flex items-center justify-between">
        <div>
          <Link to={`/clientes/${id}`} className="type-caption text-text-tertiary">
            ← Voltar ao cliente
          </Link>
          <h1 className="type-h1 mt-1 text-text-cream">Fluxo</h1>
        </div>
        <Link to={`/clientes/${id}/esteira/novo`}>
          <Button size="sm">+ Novo post</Button>
        </Link>
      </div>

      {error !== null && <p className="type-body mt-6 text-danger">{error}</p>}
      {posts === null && error === null && <Skeleton className="mt-6 h-40" />}

      {posts !== null && (
        <div className="mt-6 flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((status) => {
            const items = posts.filter((p) => p.status === status);
            return (
              <div key={status} className="w-56 shrink-0">
                <div className="flex items-center justify-between">
                  <span className="type-label text-text-secondary">
                    {statusLabel(status, "admin")}
                  </span>
                  <span className="type-caption text-text-tertiary">{items.length}</span>
                </div>
                <div className="mt-3 flex flex-col gap-2">
                  {items.map((p) => (
                    <Link
                      key={p.id}
                      to={`/clientes/${id}/posts/${p.id}`}
                      className="block rounded-[var(--radius-control)] border border-border-subtle bg-surface-raised p-2 transition-colors hover:border-border-strong"
                    >
                      <div className="flex gap-2">
                        {p.media[0] !== undefined && (
                          <img
                            src={p.media[0].publicUrl}
                            alt=""
                            className="h-12 w-12 shrink-0 rounded-[var(--radius-control-sm)] object-cover"
                          />
                        )}
                        <div className="min-w-0">
                          <PurposeBadge purpose={p.purpose} />
                          <p className="type-caption mt-1 line-clamp-2 text-text-secondary">
                            {p.caption === "" ? "—" : p.caption}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                  {items.length === 0 && (
                    <div className="rounded-[var(--radius-control)] border border-dashed border-border-subtle p-3 text-center">
                      <p className="type-caption text-text-tertiary">Nenhum post</p>
                      {status === "draft" && (
                        <Link
                          to={`/clientes/${id}/esteira/novo`}
                          className="type-caption mt-1 inline-block text-gold hover:underline"
                        >
                          + Criar rascunho
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}
